// Super-admin licensing — org list.
// Pulls /api/admin/licensing/orgs (single round-trip with summary
// counts) + /api/admin/licensing/breaches and renders 4 KPI tiles +
// a table of orgs. Click a row → drill into /super/licensing/:org_id.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ApartmentIcon from "@mui/icons-material/Apartment";

import {
  TOKENS,
  PageHeader,
  StatTile,
  SectionCard,
  StatusPill,
  EmptyState,
  Monogram,
  RowMenu,
} from "../../../components/pages/_new/super/design";
import {
  getOrgs,
  getBreaches,
} from "../../../services/licensingService";

const thSx = {
  padding: "10px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.bgSunken,
  color: TOKENS.fg2,
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const tdSx = {
  padding: "12px 14px",
  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
  fontSize: 13,
  color: TOKENS.fg1,
};

// Pill helper for any-of (active/stale/expiring).
const OrgStatusPill = ({ row }) => {
  if ((row.staleCount || 0) > 0) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 24,
          px: "10px",
          borderRadius: 999,
          background: TOKENS.dangerBg,
          color: TOKENS.dangerInk,
          border: `1px solid ${TOKENS.dangerBorder}`,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.danger }} />
        {row.staleCount} stale
      </Box>
    );
  }
  if ((row.expiringSoonCount || 0) > 0) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 24,
          px: "10px",
          borderRadius: 999,
          background: TOKENS.warningBg,
          color: TOKENS.warningInk,
          border: `1px solid ${TOKENS.warningBorder}`,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.warning }} />
        {row.expiringSoonCount} expiring
      </Box>
    );
  }
  if ((row.activeCount || 0) > 0) return <StatusPill status="Active" />;
  return <StatusPill status="Pending" />;
};

const LicensingIndex = () => {
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [orgsRes, breachesRes] = await Promise.all([
        getOrgs(),
        getBreaches(),
      ]);
      setOrgs(orgsRes?.data?.orgs || []);
      setBreaches(breachesRes?.data?.breaches || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load licensing data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const stats = useMemo(() => {
    const total = orgs.length;
    let withActive = 0;
    let withStale = 0;
    let withExpiring = 0;
    for (const o of orgs) {
      if ((o.activeCount || 0) > 0) withActive++;
      if ((o.staleCount || 0) > 0) withStale++;
      if ((o.expiringSoonCount || 0) > 0) withExpiring++;
    }
    return { total, withActive, withStale, withExpiring };
  }, [orgs]);

  const headerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button
        onClick={fetchAll}
        startIcon={<RefreshIcon style={{ width: 16, height: 16 }} />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 12.5,
          color: TOKENS.fg1,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusXs}px`,
          px: 1.5,
          py: 0.5,
          "&:hover": { background: TOKENS.bgSunken },
        }}
      >
        Refresh
      </Button>
    </Stack>
  );

  const subtitle = loading
    ? "Loading…"
    : `${orgs.length} organisation${orgs.length === 1 ? "" : "s"} · ${breaches.length} active breach${breaches.length === 1 ? "" : "es"}`;

  return (
    <Box>
      <Helmet title="Licensing" />
      <PageHeader title="Licensing" subtitle={subtitle} actions={headerActions} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<ApartmentIcon fontSize="small" />}
            iconBg={TOKENS.primaryTint}
            iconInk={TOKENS.primaryInk}
            value={stats.total}
            label="Total organisations"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            iconBg={TOKENS.successBg}
            iconInk={TOKENS.successInk}
            value={stats.withActive}
            label="With active SKUs"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<WarningAmberIcon fontSize="small" />}
            iconBg={TOKENS.warningBg}
            iconInk={TOKENS.warningInk}
            value={stats.withExpiring}
            label="Expiring soon"
            sub="Within 14 days"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<EventBusyIcon fontSize="small" />}
            iconBg={TOKENS.dangerBg}
            iconInk={TOKENS.dangerInk}
            value={stats.withStale}
            label="With stale SKUs"
            sub="One or more expired"
          />
        </Grid>
      </Grid>

      <SectionCard
        title="Organisations"
        description={loading ? "Loading…" : `${orgs.length} total`}
      >
        {error ? (
          <Box
            sx={{
              p: 2,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.dangerBg,
              border: `1px solid ${TOKENS.dangerBorder}`,
              color: TOKENS.dangerInk,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Failed to load licensing data. {error}</span>
            <Button
              size="small"
              onClick={fetchAll}
              sx={{ textTransform: "none", color: TOKENS.dangerInk, fontWeight: 600 }}
            >
              Retry
            </Button>
          </Box>
        ) : orgs.length === 0 && !loading ? (
          <EmptyState
            icon={<WorkspacePremiumIcon />}
            title="No organisations found"
            body="Once you create an organisation, its licensing posture will appear here."
          />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Organisation</TableCell>
                  <TableCell sx={thSx} align="right">SKUs</TableCell>
                  <TableCell sx={thSx} align="right">Active</TableCell>
                  <TableCell sx={thSx} align="right">Breaches</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={thSx} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {orgs.map((row) => (
                  <TableRow
                    key={row.orgId}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/super/licensing/${encodeURIComponent(row.orgId)}`)}
                  >
                    <TableCell sx={tdSx}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Monogram name={row.title || ""} size={32} radius={8} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: TOKENS.fg1 }}>
                            {row.title || "—"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: TOKENS.fg3,
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            }}
                          >
                            {row.orgId}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={tdSx} align="right">{row.skuCount ?? 0}</TableCell>
                    <TableCell sx={tdSx} align="right">{row.activeCount ?? 0}</TableCell>
                    <TableCell sx={tdSx} align="right">
                      {(row.activeBreachCount || 0) > 0 ? (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            height: 22,
                            px: "8px",
                            borderRadius: 999,
                            background: TOKENS.dangerBg,
                            color: TOKENS.dangerInk,
                            border: `1px solid ${TOKENS.dangerBorder}`,
                            fontSize: 11.5,
                            fontWeight: 600,
                            lineHeight: 1,
                          }}
                        >
                          {row.activeBreachCount}
                        </Box>
                      ) : (
                        <Typography component="span" sx={{ fontSize: 13, color: TOKENS.fg3 }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tdSx}><OrgStatusPill row={row} /></TableCell>
                    <TableCell sx={tdSx} align="right" onClick={(e) => e.stopPropagation()}>
                      <RowMenu
                        actions={[
                          {
                            key: "view",
                            label: "View licensing",
                            onClick: () =>
                              navigate(`/super/licensing/${encodeURIComponent(row.orgId)}`),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </SectionCard>
    </Box>
  );
};

export default LicensingIndex;
